import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { usePersistedState } from "@/lib/persist";
import { slaOverzichtNavOp } from "@/lib/overzichtNav";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, AlertTriangle, Trash2, ChevronRight, ChevronDown, Archive, ArchiveRestore } from "lucide-react";
import { useSortable } from "@/lib/sortable";
import { LaadBalk, TabelLaadRij } from "@/components/LaadIndicator";
import { ProductImportExport } from "@/components/ProductImportExport";
import { NieuwProductDialog } from "@/components/NieuwProductDialog";
import { MergeDuplicates } from "@/components/MergeDuplicates";
import { FillEan } from "@/components/FillEan";
import { ConfirmDeleteDialog } from "@/components/ConfirmDelete";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const MERKEN_VAST = [
  { key: "alle", label: "Alle" },
  { key: "gearchiveerd", label: "Gearchiveerd" },
];

export default function Producten() {
  const { user } = useAuth();
  const isAdmin = user?.rol === "admin";
  const [items, setItems] = useState([]);       // actieve producten
  const [archived, setArchived] = useState([]);  // gearchiveerde producten
  const [q, setQ] = usePersistedState("producten:q", "");
  const [onderMin, setOnderMin] = usePersistedState("producten:onderMin", false);
  const [attentie, setAttentie] = usePersistedState("producten:attentie", false);
  const [merk, setMerk] = usePersistedState("producten:merk", "alle");
  const [expanded, setExpanded] = useState(() => new Set());
  const [page, setPage] = useState(1);
  const [delTarget, setDelTarget] = useState(null);
  const [loaded, setLoaded] = useState({ act: false, arch: false });
  const perPage = 25;
  const navigate = useNavigate();

  const load = async () => {
    setLoaded({ act: false, arch: false });
    // Actieve producten direct; gearchiveerde op de achtergrond
    const { data: act } = await api.get("/producten", { params: { q, onder_minimum: onderMin || undefined } });
    setItems(act); setPage(1);
    setLoaded(prev => ({ ...prev, act: true }));
    api.get("/producten", { params: { q, gearchiveerd: 1 } })
      .then(({ data: arch }) => { setArchived(arch); setLoaded(prev => ({ ...prev, arch: true })); })
      .catch(e => { console.warn("Gearchiveerde producten laden mislukt:", e); setLoaded(prev => ({ ...prev, arch: true })); });
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, onderMin]);

  const removeProduct = async () => {
    if (!delTarget) return;
    try {
      await api.delete(`/producten/${delTarget.id}`);
      toast.success("Verwijderd");
      load();
    } catch { toast.error("Verwijderen mislukt"); }
  };

  const setArchief = async (p, val) => {
    try {
      await api.patch(`/producten/${p.id}`, { gearchiveerd: val });
      toast.success(val ? "Gearchiveerd" : "Hersteld");
      load();
    } catch { toast.error("Mislukt"); }
  };

  const isArchief = merk === "gearchiveerd";
  const source = isArchief ? archived : items;

  const childrenByMoeder = useMemo(() => {
    const m = {};
    for (const p of source) {
      const ms = (p.moeder_sku || "").trim().toLowerCase();
      if (ms) (m[ms] ||= []).push(p);
    }
    return m;
  }, [source]);

  const merkTabs = useMemo(() => {
    const telling = {};
    for (const p of items) {
      const m = (p.merk || "").trim() || "Overig";
      telling[m] = (telling[m] || 0) + 1;
    }
    const merken = Object.keys(telling).sort((a, b) => (a === "Overig" ? 1 : b === "Overig" ? -1 : telling[b] - telling[a]));
    return [MERKEN_VAST[0], ...merken.map((m) => ({ key: m, label: m })), MERKEN_VAST[1]];
  }, [items]);

  const merkCount = useMemo(() => {
    const c = { alle: items.length, gearchiveerd: archived.length };
    for (const p of items) {
      const m = (p.merk || "").trim() || "Overig";
      c[m] = (c[m] || 0) + 1;
    }
    return c;
  }, [items, archived]);

  const topLevel = useMemo(() =>
    source.filter(p =>
      !(p.moeder_sku || "").trim() &&
      (isArchief || merk === "alle" || ((p.merk || "").trim() || "Overig") === merk) &&
      (isArchief || !attentie || p.bestel_attentie)
    ), [source, merk, attentie, isArchief]);

  const { sortedRows, SortHeader } = useSortable(topLevel, "sku");
  const paged = sortedRows.slice((page - 1) * perPage, page * perPage);
  const totalPages = Math.max(1, Math.ceil(sortedRows.length / perPage));

  const [teValideren, setTeValideren] = useState({ aantal: 0, producten: [] });
  const [toonValidatie, setToonValidatie] = useState(false);
  useEffect(() => { api.get("/producten/claims-te-valideren").then(({ data }) => setTeValideren(data || { aantal: 0, producten: [] })).catch(() => {}); }, []);

  // Bewaar de volledige zichtbare volgorde (moeder + varianten) voor detail-navigatie
  const openProduct = (pid) => {
    const flat = [];
    for (const p of sortedRows) {
      flat.push(p.id);
      for (const c of (childrenByMoeder[(p.sku || "").toLowerCase()] || [])) flat.push(c.id);
    }
    slaOverzichtNavOp("producten", flat, window.location.pathname + window.location.search);
    navigate(`/producten/${pid}`);
  };

  const fmtD = (s) => (s ? new Date(s).toLocaleDateString("nl-NL", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");
  const rowClass = (p) => {
    if (isArchief) return "opacity-70";
    if (p.weken_tot_bestelmoment != null && p.weken_tot_bestelmoment <= 0) return "bg-destructive/10 hover:bg-destructive/15";
    if (p.bestel_attentie) return "bg-accent/10 hover:bg-accent/20";
    return "";
  };
  const toggle = (sku) => setExpanded((s) => { const n = new Set(s); n.has(sku) ? n.delete(sku) : n.add(sku); return n; });
  const colSpan = isAdmin ? 11 : 10;

  const loadDone = [loaded.act, loaded.arch].filter(Boolean).length;
  const loadPct = Math.round((loadDone / 2) * 100);
  const fullyLoaded = loadDone === 2;
  const tabLoading = isArchief ? !loaded.arch : !loaded.act;

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-sm text-muted-foreground">Catalogus</div>
          <h1 className="font-display text-4xl font-medium tracking-tight mt-1">Producten</h1>
          <p className="text-sm text-muted-foreground mt-1">{sortedRows.length} hoofdproduct(en) · {items.length} actieve SKU's</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && <MergeDuplicates onMerged={load} />}
          {isAdmin && <FillEan onFilled={load} />}
          <ProductImportExport onImported={load} />
          {isAdmin && <NieuwProductDialog onCreated={load} />}
        </div>
      </div>

      {!fullyLoaded && (
        <LaadBalk pct={loadPct} label="Producten laden"
          fasen={[{ label: "actief", done: loaded.act }, { label: "archief", done: loaded.arch }]} />
      )}

      {teValideren.aantal > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-3" data-testid="claims-validatie-banner">
          <button type="button" onClick={() => setToonValidatie(v => !v)}
            className="w-full flex items-center justify-between gap-2 text-sm font-medium text-amber-800"
            data-testid="claims-validatie-toggle">
            <span className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              {teValideren.aantal} product(en) met claims die nog gevalideerd moeten worden
            </span>
            <span className="text-xs underline">{toonValidatie ? "verbergen" : "bekijken"}</span>
          </button>
          {toonValidatie && (
            <div className="mt-3 divide-y divide-amber-200 max-h-72 overflow-auto" data-testid="claims-validatie-lijst">
              {teValideren.producten.map((p) => (
                <button key={p.id} type="button" onClick={() => openProduct(p.id)}
                  className="w-full text-left py-2 px-1 hover:bg-amber-100/60 rounded flex items-start gap-3"
                  data-testid={`claims-valideren-${p.id}`}>
                  <span className="font-mono text-xs text-amber-700 w-28 shrink-0">{p.sku}</span>
                  <span className="text-sm flex-1 min-w-0">
                    <span className="font-medium">{p.naam}</span>
                    <span className="block text-xs text-amber-700">
                      {p.talen.map(t => `${t.kanaal === "platform" ? "Platform" : "Website"} · ${t.taal}: ${t.claims}`).join("  ·  ")}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Merk-tabs */}
      <div className="flex items-center gap-1 border-b border-border" data-testid="merk-tabs">
        {merkTabs.map((m) => (
          <button
            key={m.key}
            data-testid={`merk-tab-${m.key}`}
            onClick={() => { setMerk(m.key); setPage(1); }}
            className={`px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors ${
              merk === m.key ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {m.label} <span className="text-xs text-muted-foreground">({merkCount[m.key] ?? 0})</span>
          </button>
        ))}
      </div>

      <div className="flex gap-3 flex-wrap items-center bg-card border border-border rounded-xl p-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Zoek op SKU, naam, EAN…" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="products-search" />
        </div>
        {!isArchief && (
          <Button variant={attentie ? "default" : "outline"} onClick={() => { setAttentie(!attentie); setPage(1); }} data-testid="filter-bestellen-nodig">
            <AlertTriangle className="h-4 w-4 mr-2" /> Bestellen nodig
          </Button>
        )}
      </div>

      {isArchief && (
        <div className="text-sm text-muted-foreground bg-secondary/40 border border-border rounded-lg px-4 py-2.5" data-testid="archief-uitleg">
          Gearchiveerde producten tellen nog mee in de metrics, maar zijn niet selecteerbaar in verkoopkansen en verschijnen niet in de andere tabbladen.
        </div>
      )}

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-secondary/50 text-xs uppercase tracking-wide">
            <tr>
              <th className="w-8"></th>
              <SortHeader k="sku">SKU</SortHeader>
              <SortHeader k="naam">Naam</SortHeader>
              <SortHeader k="voorraad_warehouse" align="right" accessor={(p) => Number(p.voorraad_warehouse || 0)}>Eigen</SortHeader>
              <SortHeader k="voorraad_leverancier" align="right" accessor={(p) => Number(p.voorraad_leverancier || 0)}>Leverancier</SortHeader>
              <SortHeader k="voorraad_channeldock" align="right" accessor={(p) => Number(p.voorraad_channeldock || 0)}>ChannelDock</SortHeader>
              <SortHeader k="voorraad_locaties_totaal" align="right" accessor={(p) => Number(p.voorraad_locaties_totaal || 0)}>Totaal</SortHeader>
              <SortHeader k="gem_maandverkoop" align="right" accessor={(p) => Number(p.gem_maandverkoop || 0)}>Gem/mnd</SortHeader>
              <SortHeader k="besteldatum" accessor={(p) => p.besteldatum || "9999"}>Besteldatum</SortHeader>
              <SortHeader k="out_of_stock_datum" accessor={(p) => p.out_of_stock_datum || "9999"}>Out of stock</SortHeader>
              {isAdmin && <th className="w-20"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paged.map((p) => {
              const skuL = (p.sku || "").toLowerCase();
              const kids = childrenByMoeder[skuL] || [];
              const isOpen = expanded.has(p.sku);
              return (
                <React.Fragment key={p.id}>
                  <tr className={`hover:bg-secondary/40 cursor-pointer ${rowClass(p)}`} onClick={() => openProduct(p.id)} data-testid={`product-row-${p.id}`}>
                    <td className="pl-3 pr-0 py-3" onClick={(e) => { e.stopPropagation(); if (kids.length) toggle(p.sku); }}>
                      {kids.length > 0 && (
                        <button data-testid={`expand-${p.id}`} className="text-muted-foreground hover:text-foreground" title={`${kids.length} variant(en)`}>
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      )}
                    </td>
                    <td className="p-3 font-mono text-xs">
                      {p.sku}
                      {kids.length > 0 && <Badge variant="outline" className="ml-2 text-[10px]">+{kids.length}</Badge>}
                    </td>
                    <td className="p-3 font-medium">{p.naam}</td>
                    <td className="p-3 text-right text-muted-foreground">{p.voorraad_warehouse ?? 0}</td>
                    <td className="p-3 text-right text-muted-foreground">{p.voorraad_leverancier ?? 0}</td>
                    <td className={`p-3 text-right ${p.channeldock_negatief ? "text-destructive font-medium" : "text-muted-foreground"}`}
                      title={p.channeldock_negatief ? "Negatieve voorraad bij ChannelDock" : undefined}
                      data-testid={`cd-voorraad-${p.id}`}>
                      {p.channeldock_negatief && <AlertTriangle className="h-3 w-3 inline mr-1" />}{p.voorraad_channeldock ?? 0}
                    </td>
                    <td className="p-3 text-right font-medium">{p.voorraad_locaties_totaal ?? 0}</td>
                    <td className="p-3 text-right text-muted-foreground">{p.gem_maandverkoop ?? 0}</td>
                    <td className="p-3 text-xs" data-testid={`besteldatum-${p.id}`}>{p.besteldatum ? <span className={p.bestel_attentie ? "text-destructive font-medium" : "text-muted-foreground"}>{fmtD(p.besteldatum)}</span> : "—"}</td>
                    <td className="p-3 text-xs text-muted-foreground">{fmtD(p.out_of_stock_datum)}</td>
                    {isAdmin && (
                      <td className="p-2 text-right whitespace-nowrap">
                        {isArchief ? (
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-primary"
                            onClick={(e) => { e.stopPropagation(); setArchief(p, false); }}
                            data-testid={`product-restore-${p.id}`} title="Herstellen">
                            <ArchiveRestore className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-foreground"
                            onClick={(e) => { e.stopPropagation(); setArchief(p, true); }}
                            data-testid={`product-archive-${p.id}`} title="Archiveren">
                            <Archive className="h-4 w-4" />
                          </Button>
                        )}
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={(e) => { e.stopPropagation(); setDelTarget(p); }}
                          data-testid={`product-delete-${p.id}`} title="Verwijderen">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    )}
                  </tr>
                  {isOpen && kids.map((c) => (
                    <tr key={c.id} className="bg-secondary/20 hover:bg-secondary/40 cursor-pointer text-sm" onClick={() => openProduct(c.id)} data-testid={`child-row-${c.id}`}>
                      <td></td>
                      <td className="p-3 pl-6 font-mono text-xs text-muted-foreground border-l-2 border-primary/30">↳ {c.sku}</td>
                      <td className="p-3 text-muted-foreground">{c.naam}</td>
                      <td className="p-3 text-right text-muted-foreground">{c.voorraad_warehouse ?? 0}</td>
                      <td className="p-3 text-right text-muted-foreground">{c.voorraad_leverancier ?? 0}</td>
                      <td className="p-3 text-right text-muted-foreground">{c.voorraad_channeldock ?? 0}</td>
                      <td className="p-3 text-right text-muted-foreground">{c.voorraad_locaties_totaal ?? 0}</td>
                      <td className="p-3 text-right text-muted-foreground">{c.gem_maandverkoop ?? 0}</td>
                      <td className="p-3 text-xs text-muted-foreground">{fmtD(c.besteldatum)}</td>
                      <td className="p-3 text-xs text-muted-foreground">{fmtD(c.out_of_stock_datum)}</td>
                      {isAdmin && <td></td>}
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
            {paged.length === 0 && <TabelLaadRij colSpan={colSpan} loading={tabLoading} leeg="Geen producten." />}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-3 border-t border-border text-sm">
            <span className="text-muted-foreground">{sortedRows.length} resultaten · pagina {page}/{totalPages}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Vorige</Button>
              <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Volgende</Button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={!!delTarget}
        onOpenChange={(o) => { if (!o) setDelTarget(null); }}
        title="Product verwijderen?"
        description={delTarget ? `Je staat op het punt "${delTarget.naam}" (${delTarget.sku}) definitief te verwijderen. Dit kan niet ongedaan worden gemaakt.` : ""}
        onConfirm={removeProduct}
        testid="product-list-delete-dialog"
      />
    </div>
  );
}
